import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';

export function parseInputText(text: string): CalendarDate | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const parts = trimmed.split('/').filter((p) => p.length > 0);
  const nums = parts.map((p) => parseInt(p, 10));
  if (nums.some(isNaN)) return null;

  const now = today(getLocalTimeZone());

  // CalendarDate clamps out-of-range values rather than throwing (e.g., month 13 → 12,
  // Feb 30 → Feb 28). No try/catch needed; the only fallback is the null below.
  if (nums.length === 1) {
    return new CalendarDate(now.year, now.month, nums[0]);
  }
  if (nums.length === 2) {
    return new CalendarDate(now.year, nums[0], nums[1]);
  }
  if (nums.length >= 3) {
    let year = nums[2];
    if (parts[2].length === 2) year += 2000;
    return new CalendarDate(year, nums[0], nums[1]);
  }
  return null;
}

export function parseTimeText(
  text: string,
  granularity: 'day' | 'hour' | 'minute' | 'second'
): { hour: number; minute: number; second: number } | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  // Extract AM/PM suffix
  const upper = trimmed.toUpperCase();
  const isPM = upper.endsWith('PM');
  const isAM = upper.endsWith('AM');
  const timePart = trimmed.replace(/\s*(AM|PM)\s*$/i, '').trim();

  const parts = timePart.split(':').filter((p) => p.length > 0);
  const nums = parts.map((p) => parseInt(p, 10));
  if (nums.some(isNaN)) return null;

  let hour = nums[0] ?? 0;
  const minute = nums[1] ?? 0;
  const second = nums[2] ?? 0;

  // Convert 12-hour to 24-hour
  if (isPM || isAM) {
    if (hour < 1 || hour > 12) return null;
    if (isPM && hour !== 12) hour += 12;
    if (isAM && hour === 12) hour = 0;
  } else {
    if (hour < 0 || hour > 23) return null;
  }

  if (minute < 0 || minute > 59) return null;
  if (granularity === 'second' && (second < 0 || second > 59)) return null;

  return { hour, minute, second };
}
